import { Beef } from '@/services/beef'

export interface User {
  email: string
  password: string
}

export enum UserType {
  staff = 'staff',
  property = 'property',
  partner = 'partner',
  enterprise = 'enterprise',
}

export enum Provider {
  auth0 = 'auth0',
  authx = 'authx',
}

export enum PasskeyLoginMode {
  Standard = 'login-id-standard',
  StepUp = 'login-id-step-up',
  Enrolment = 'login-id-enrolment',
}

export type UserIdentity = Beef.AuthenticateResponse

export interface UserIdentityTokenPayload {
  email: string
  provider: Provider
  partnerCode: string
}

export interface ActivateInput {
  token: string
  password: string
  confirmPassword?: string
  secretQuestion: string
  secretAnswer: string
}

export enum MfaRegistrationType {
  SMMOBILE = 'smmobile',
  TOTP = 'totp',
}

export enum UserState {
  PASSWORD_EXPIRED = 'PASSWORD_EXPIRED',
  AUTHENTICATED = 'AUTHENTICATED',
  MFA_REGISTRATION_RECOMMENDED = 'MFA_REGISTRATION_RECOMMENDED',
  MFA_REGISTRATION_REQUIRED = 'MFA_REGISTRATION_REQUIRED',
  // user is not MFA-required but has opted in to set up MFA (temporary MFA setup)
  MFA_REGISTRATION_OPTIONAL = 'MFA_REGISTRATION_OPTIONAL',
  MFA_VERIFICATION_REQUIRED = 'MFA_VERIFICATION_REQUIRED',
  TRUSTED_DEVICE_VERIFICATION_REQUIRED = 'TRUSTED_DEVICE_VERIFICATION_REQUIRED',
}

export enum ResetPasswordState {
  ACTIVATE = 'ACTIVATE',
  RESET = 'RESET',
}

export interface VerifyEmailInput {
  email: string | null
}

export enum MobileMfaAuthenticationError {
  REJECTED = 'REJECTED',
  TIMEOUT = 'TIMEOUT',
  RATE_LIMITED = 'RATE_LIMITED',
  FAILED = 'FAILED',
}

export enum MobileMfaSetupError {
  SETUP_FAILED = 'SETUP_FAILED',
  SETUP_TIMEOUT_FAILED = 'SETUP_TIMEOUT_FAILED',
}

export enum MfaPurpose {
  mfaReset = 'for-mfa-reset',
  mfaVerification = 'for-mfa-verification',
  mfaLogin = 'for-mfa-login',
  mfaRegistration = 'for-mfa-registration',
  passkeyEnrollment = 'passkey-enrollment',
  passkeyRegistration = 'passkey-registration',
}

export enum PasskeyPurpose {
  // user will redirect to auth0 to register passkey
  passkeyRegistration = 'passkey-registration',
  // user has finished enrolling a passkey with auth0 and will redirect back to app to finish enrollment process
  passkeyEnrollment = 'passkey-enrollment',
  // user will redirect to auth0 to authenticate with passkey for step up authentication
  passkeyStepUpAuthentication = 'passkey-step-up-authentication',
  // user has finished authenticating with a passkey with auth0 and will redirect back to app to finish step up authentication process
  passkeyStepUpSuccess = 'passkey-step-up-success',
  // user has cancelled the step-up in universal login
  passkeyStepUpCancelled = 'passkey-step-up-cancelled',
  // user has cancelled the enrollment in universal login
  passkeyEnrollmentCancelled = 'passkey-enrollment-cancelled',
}

// values of the `passkeySetup` query param appended to the product redirect after enrolment
export enum PasskeySetupResult {
  success = 'success',
  // the user tried to re-enroll a passkey this device already holds (SS-7534)
  passkeyAlreadyExist = 'passkey-already-exist',
}

export enum MfaRequestType {
  NUMBER_CHALLENGE = 'numberChallenge',
  SETUP_MASTER_DEVICE = 'setupMasterDevice',
  MASTER_DEVICE_LOGIN = 'masterDeviceLogin',
}

export enum MFA_VERIFICATION_EVENT_TYPE {
  mfaVerificationSuccess = 'MFA_VERIFICATION_SUCCESS',
  mfaMobileVerificationFailed = 'MFA_MOBILE_VERIFICATION_FAILED',
  mfaBackEvent = 'MFA_BACK_EVENT',
}

export enum AuthPurpose {
  reAuth = 'reauth',
  passkeyLogin = 'passkey-login',
}

export enum TrustedDevicePurpose {
  trustedDeviceEnrollment = 'trusted-device-enrollment',
  trustedDeviceChallenge = 'trusted-device-challenge',
}
